import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import LabAssetsAPI from "@/api/lab-assets";
import { ConnectionFailurePayload } from "@/api/lab-assets/lab-assets.types";
import { toast } from "@/utils/toastService";
import { labAssetQueryKeys } from "../labAssets.queryKeys";
import { getLabAssetDisplayName } from "../labAssets.utils";
import { LabAsset } from "../labAssets.types";

interface DeleteLabAssetModalProps {
  show: boolean;
  onClose: () => void;
  // Fires only after the API confirms the delete; the caller decides where
  // to go next (the details page navigates back to the list).
  onDelete?: (deleted: LabAsset) => void;
  defaultInfo: LabAsset;
}

// Layout mirrors DeleteEquipmentModal. connection.js resolves failures as
// { success: false } instead of rejecting; the interceptor toast is skipped
// so this modal owns its error surface (same as useUpdateLabAsset). A failed
// delete toasts and leaves the modal open for a retry. A 404 is silent: the
// asset is already gone (deleted elsewhere), so it gets the same cache
// cleanup as a success instead of stranding the caller on a stale record.
export const DeleteLabAssetModal: React.FC<DeleteLabAssetModalProps> = ({
  show,
  onClose,
  onDelete,
  defaultInfo,
}) => {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const label =
    defaultInfo.asset_number ||
    getLabAssetDisplayName(defaultInfo) ||
    "Unnamed Asset";

  const handleSubmit = async () => {
    setSubmitting(true);
    let result: ConnectionFailurePayload | undefined;
    try {
      result = (await LabAssetsAPI.deleteAssetItem(defaultInfo.uuid, {
        skipErrorToast: true,
      })) as ConnectionFailurePayload | undefined;
    } finally {
      setSubmitting(false);
    }
    if (result?.success === false && result.error?.response?.status !== 404) {
      toast.error(
        result.error?.response?.data?.message ||
          "An error occurred while deleting this asset."
      );
      return;
    }

    queryClient.removeQueries({
      queryKey: labAssetQueryKeys.item(defaultInfo.uuid),
    });
    queryClient.invalidateQueries({
      queryKey: labAssetQueryKeys.infiniteLists,
    });
    onDelete?.(defaultInfo);
    onClose();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete Asset</Modal.Title>
      <div style={{ fontWeight: 600, margin: "20px 0px" }}>
        Are you sure you want to delete this asset: "{label}"?
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Delete Asset
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
